// 🔴 값은 **정의한 파일에서** — 배럴(`./index`)로 돌면 순환 참조라 순서에 따라 undefined 가 된다
import { HERO_CODE_ALPHABET, HERO_CODE_LENGTH } from "./hero";

/**
 * 🏫 **교실에서 쓰기** — 한 태블릿을 여러 아이가 돌려 쓴다.
 *
 * ## 무엇이 문제였나
 * 지금까지는 아이 한 명이 자기 기기에서 노는 것을 전제로 만들었다. 학교는 다르다 —
 * 태블릿을 반마다 돌려 쓰는데, 히어로가 기기에 하나만 담기니 **다음 아이가 만드는 순간
 * 앞 아이 것이 조용히 덮인다.** 아이는 다음 주에 자기 친구를 잃어버린다.
 *
 * ## 이미 있는 것으로 푼다
 * 여섯 자리 코드로 서버에서 히어로를 되살리는 길이 이미 있다(`/h`). 수업 끝에 코드를
 * 적어 두고 다음 시간에 그 코드로 부르면 된다. 여기서는 그 코드를 **사람이 다루기 좋게**
 * 만드는 판정만 둔다 — 화면마다 다시 짜면 갈린다(CLAUDE.md).
 */

/** 코드를 사람이 옮겨 적기 좋게 나눈 모양. `A7K3P9` → `A7K 3P9` */
export function formatHeroCode(code: string): string {
  const c = (code ?? "").toUpperCase();
  if (c.length !== HERO_CODE_LENGTH) return c;
  // 🔴 셋씩 끊는다. 여섯 자를 한 덩이로 두면 아이가 적다가 자리를 놓친다.
  return `${c.slice(0, 3)} ${c.slice(3)}`;
}

/**
 * 사람이 적어 온 코드를 받아 준다.
 *
 * 아이가 적은 종이를 보고 치는 것이라 **공백·줄표·소문자**가 섞인다. 그걸 이유로
 * 「없는 코드예요」라고 하면, 맞게 적어 놓고도 못 들어간다.
 *
 * 🔴 다만 **없는 글자를 마음대로 고쳐 주지는 않는다.** 코드 알파벳에는 헷갈리는 글자
 * (`I·L·O·U·0·1`)가 애초에 빠져 있어서, 그런 글자가 왔다면 잘못 읽은 것이다 —
 * 짐작해서 바꾸면 **남의 히어로가 열릴 수 있다.**
 */
export function parseHeroCode(input: string): string | null {
  const cleaned = (input ?? "").toUpperCase().replace(/[\s-]/g, "");
  if (cleaned.length !== HERO_CODE_LENGTH) return null;
  for (const ch of cleaned) if (!HERO_CODE_ALPHABET.includes(ch)) return null;
  return cleaned;
}

/**
 * 코드를 잘못 적었을 때 **무엇이 잘못됐는지** 알려 줄 말.
 *
 * 「코드를 확인해 주세요」만 띄우면 아이도 선생님도 무엇을 고쳐야 할지 모른다.
 * 길이가 문제인지, 없는 글자를 적었는지는 알려 줄 수 있다.
 */
export function heroCodeHint(input: string): string | null {
  const cleaned = (input ?? "").toUpperCase().replace(/[\s-]/g, "");
  if (cleaned.length === 0) return "코드를 적어 주세요.";
  if (cleaned.length < HERO_CODE_LENGTH) return `${HERO_CODE_LENGTH}글자예요. 지금은 ${cleaned.length}글자.`;
  if (cleaned.length > HERO_CODE_LENGTH) return `${HERO_CODE_LENGTH}글자예요. 너무 길어요.`;
  const bad = [...cleaned].filter((ch) => !HERO_CODE_ALPHABET.includes(ch));
  if (bad.length > 0) {
    // 어떤 글자가 문제인지 짚어 준다. 「O 를 적었는데 사실 Q 였다」 같은 일이 흔하다.
    return `「${[...new Set(bad)].join(" ")}」 는 코드에 없는 글자예요. 다시 봐 주세요.`;
  }
  return null;
}

/**
 * 👋 다음 친구에게 넘기기 전에 하는 말.
 *
 * 🔴 **코드를 안 적었으면 되돌릴 수 없다.** 그래서 「넘길까요?」만 묻지 않고
 * 코드를 화면에 다시 보여 주며 묻는다. 지우기와 같은 무게의 일이다.
 */
export function handoffPrompt(code: string | undefined, nickname?: string): string {
  const who = nickname?.trim() || "이 친구";
  if (!code) {
    return [
      `${who}는 아직 코드가 없어요.`,
      "",
      "지금 넘기면 다시 부를 수 없어요.",
      "그래도 넘길까요?",
    ].join("\n");
  }
  return [
    `${who}의 코드는 ${formatHeroCode(code)} 예요.`,
    "",
    "종이에 적어 두셨나요?",
    "적어 두지 않으면 다시 부를 수 없어요.",
    "",
    "다음 친구에게 넘길까요?",
  ].join("\n");
}

/**
 * 🧹 **[처음부터 다시 하기] 전에 하는 말.**
 *
 * 🔴 예전 문구는 「내 히어로·모은 별·랠리 완주를 모두 지울까요?」였다.
 * **그림책과 코드를 안 적어 놨다.** 그런데 지워지는 것은 그 둘이기도 하다:
 *
 * - **코드** — 이걸 잃으면 서버에 있는 그림책·도장으로 **돌아갈 길이 없다.**
 *   가입이 없어서 코드가 유일한 열쇠다.
 * - **그림책** — 수업에서 쓴 글은 이 기기에도 있고 서버에도 있는데,
 *   코드를 잃으면 서버 것은 **주인 없는 글**이 된다(선생님 화면에서도 못 찾는다).
 *
 * 그래서 넘기기(`handoffPrompt`)와 **같은 무게**로 묻는다 — 코드를 보여 주고,
 * 적어 뒀는지 확인하고, 그림책이 있으면 그것도 말한다.
 * 학교 태블릿에서 아이가 눌러 볼 수 있는 자리라 더 그렇다.
 */
export function resetPrompt(opts: {
  code?: string;
  /** 이 기기에 쓴 그림책이 있나(쪽 수가 아니라 있고 없고만). */
  hasStory?: boolean;
  /** 🎙 보호자가 녹음한 목소리가 있나(리서치 최종안 ⑦) — 되돌릴 수 없는 것이라 미리 말한다. */
  hasVoice?: boolean;
}): string {
  const lines: string[] = [];
  if (opts.hasStory) {
    lines.push("이 기기에 쓴 **그림책**도 함께 지워져요.");
    lines.push("");
  }
  if (opts.hasVoice) {
    lines.push("보호자가 녹음한 **목소리**도 함께 지워져요.");
    lines.push("");
  }
  if (opts.code) {
    lines.push(`내 코드는 ${formatHeroCode(opts.code)} 예요.`);
    lines.push("종이에 적어 두지 않으면 다시 부를 수 없어요.");
  } else {
    lines.push("아직 코드가 없어서 다시 부를 수 없어요.");
  }
  lines.push("");
  lines.push("내 히어로·모은 별·랠리 완주를 모두 지울까요?");
  return lines.join("\n");
}

/* ────────────────────────────────────────────────────────────────
   📚 수업 모드 — 수업에 방해되는 것을 끈다
   ──────────────────────────────────────────────────────────────── */

/**
 * 주소 뒤에 붙이는 이름. `?class=1` 로 켜고 `?class=0` 으로 끈다.
 *
 * 🔴 **영문으로 둔다.** 선생님이 알림장·메신저로 주소를 옮겨 적는데,
 * 한글 이름은 앱마다 다르게 인코딩돼 링크가 깨진다.
 */
export const CLASS_MODE_PARAM = "class";

/** 수업 모드가 그 탭에서 유지되는 자리. 링크를 타고 옮겨 다녀도 남아 있어야 한다. */
export const CLASS_MODE_KEY = "gc_class_mode";

/**
 * 수업 중에 **숨길 것**.
 *
 * 수업 중에 아이 스물다섯이 각자 굿즈샵과 유튜브로 흩어지면 선생님이 수업을 못 한다.
 * 그리고 학교는 **수업 시간에 상품이 보이는 것**에 특히 민감하다.
 *
 * 🔴 **설정과 도서관은 남긴다.** 소리를 끄고 켜는 일은 교실에서 오히려 더 필요하고,
 * 그림책은 수업의 본체다. 「수업에 쓰는 것」만 남기는 것이지 다 잠그는 게 아니다.
 */
export const CLASS_HIDDEN_KEYS = ["store", "community", "event"] as const;

/** 수업 중에 막을 길(주소). 랠리는 밖에 나가야 하는 것이라 교실에서 못 한다. */
export const CLASS_HIDDEN_ROUTES = ["/goods", "/community", "/rally"] as const;

/** 이 메뉴를 수업 중에 보여줄까. */
export function classModeAllows(key: string, on: boolean): boolean {
  if (!on) return true;
  return !(CLASS_HIDDEN_KEYS as readonly string[]).includes(key);
}

/** 이 주소를 수업 중에 열어도 되나. */
export function classModeAllowsRoute(path: string, on: boolean): boolean {
  if (!on) return true;
  return !CLASS_HIDDEN_ROUTES.some((r) => path === r || path.startsWith(`${r}/`));
}

/**
 * 주소에서 수업 모드를 읽는다. **켜기·끄기·그대로** 셋을 가른다.
 *
 * 🔴 「없으면 끈다」가 아니다. 아이가 링크를 타고 다른 화면으로 가면 주소에서
 * 파라미터가 사라지는데, 그때마다 수업 모드가 풀리면 아무 소용이 없다.
 * 주소에 **분명히 적혀 있을 때만** 바꾼다.
 */
export function classModeFromSearch(search: string): boolean | null {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get(CLASS_MODE_PARAM);
  } catch {
    return null; // 주소가 깨져 있어도 화면은 떠야 한다
  }
  if (raw === null) return null;
  const v = raw.trim().toLowerCase();
  if (v === "0" || v === "off" || v === "false") return false;
  return true; // `?class` · `?class=1` · `?class=아무거나` — 켜기로 읽는다
}

/**
 * 🏫 **「그런 반 없어요」 · 「반이 정리됐어요」 · 「아직 아무도 안 썼어요」 — 셋을 가른다.**
 *
 * 지금까지 첫째와 셋째가 똑같이 보였다. 아무도 안 연 코드로 물어도 서버는 빈 목록을 줬고,
 * 화면은 「아직 걸린 그림책이 없어요」라고만 했다. 반 코드를 **손으로 치는 자리가 세 곳**
 * (전시장·무대·선생님)이라 오타가 나는데, 그러면 왜 안 나오는지 알 길이 없다.
 *
 * 🔴 「없는 반」은 **아무 흔적도 없을 때**만이다(반 행·명단·글 모두 0). 선생님이
 * [새 반 만들기]를 안 누르고 코드만 나눠 줘도 수업은 돌아가므로, 반 행만 보고 판단하면
 * **돌아가는 수업을 없다고 말한다.** 판정은 서버(`isClassUnknown`)가 하고 여기선 말만 정한다.
 */
export function classEmptySay(o: {
  gone?: boolean;
  unknown?: boolean;
  /**
   * 🔴 **지금 못 물어봤다**(500·503·429·끊김) — 「비었다」와 다르다.
   *
   * 이 함수는 세 갈래만 갈랐다(정리됨·없는 반·비었다). 그래서 서버가 아픈 동안 전시장이
   * **「아직 걸린 그림책이 없어요」**라고 말했다 — 아이에게는 「친구들이 아무것도 안 썼다」로,
   * 어른에게는 「아무도 안 들어왔다」로 들린다. 부모 화면은 이미 이걸 가르고 있었는데
   * (`FamilyBook` 의 `offline`) 전시장만 안 갈랐다. **넷을 다 가른다.**
   */
  off?: boolean;
  /** 아이 화면(전시장)인지 — 어른 화면과 말이 다르다. */
  forChild?: boolean;
}): string {
  if (o.gone) {
    return "이 반은 정리됐어요. 아이들 그림책은 수업 때 뽑아 드린 종이에 그대로 있어요.";
  }
  if (o.unknown) {
    return "그런 반 코드를 못 찾았어요. 「별빛-3F7K」처럼 이름과 네 글자를 다시 확인해 주세요.";
  }
  if (o.off) {
    // 「없다」고 단정하지 않는다 — 아이가 안 쓴 게 아니다
    return o.forChild
      ? "지금은 친구들 그림책을 못 불러왔어요. 잠시 뒤 다시 열어 볼까요?"
      : "지금은 명단을 못 불러왔어요 — 아무도 안 들어온 게 아니에요. 잠시 뒤 다시 열어 주세요.";
  }
  return o.forChild
    ? "아직 걸린 그림책이 없어요. 친구들이 이야기를 쓰면 여기 걸려요."
    : "아직 이 반에 들어온 친구가 없어요.";
}
